import { cn } from '@/lib/utils'
import type { SystemState } from '@/api/types'

const stateConfig: Record<SystemState, { label: string; color: string; pulse: boolean; icon: string }> = {
  ready: {
    label: 'Ready',
    color: 'bg-emerald-500',
    pulse: false,
    icon: '●',
  },
  initialising: {
    label: 'Initialising',
    color: 'bg-blue-500',
    pulse: true,
    icon: '◐',
  },
  uninitialised: {
    label: 'Uninitialised',
    color: 'bg-zinc-500',
    pulse: false,
    icon: '○',
  },
  degraded: {
    label: 'Degraded',
    color: 'bg-amber-500',
    pulse: true,
    icon: '◑',
  },
}

interface StateIndicatorProps {
  state: SystemState
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  className?: string
}

export function StateIndicator({ state, size = 'md', showLabel = true, className }: StateIndicatorProps) {
  const config = stateConfig[state] || stateConfig.uninitialised
  const dotSizes = { sm: 'h-2 w-2', md: 'h-2.5 w-2.5', lg: 'h-3 w-3' }
  const textSizes = { sm: 'text-xs', md: 'text-sm', lg: 'text-base' }

  return (
    <div className={cn('inline-flex items-center gap-2', className)}>
      <span className="relative flex items-center justify-center">
        <span
          className={cn(
            'rounded-full',
            dotSizes[size],
            config.color,
            config.pulse && 'animate-pulse-soft'
          )}
        />
        {config.pulse && (
          <span
            className={cn(
              'absolute rounded-full opacity-40',
              dotSizes[size],
              config.color,
              'animate-ping'
            )}
            style={{ animationDuration: '2s' }}
          />
        )}
      </span>
      {showLabel && (
        <span className={cn('font-medium', textSizes[size])}>{config.label}</span>
      )}
    </div>
  )
}
